import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LifeBuoy } from "lucide-react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const newRequestId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Restaurant → Super Admin support request (existing Support Tickets queue).
export function SupportRequestEntry({ suffix = "", onOpen }) {
    const { t } = useTranslation();
    const { user, restaurantId } = useApp();
    const [open, setOpen] = useState(false);
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [state, setState] = useState("idle");   // idle | sending | sent | error
    const [reqId, setReqId] = useState(newRequestId);
    if (!user || user.role === "super_admin" || !user.company_id) return null;

    const close = () => {
        setOpen(false);
        if (state === "sent") { setSubject(""); setMessage(""); setState("idle"); setReqId(newRequestId()); }
    };
    const submit = async () => {
        if (state === "sending") return;
        setState("sending");
        try {
            await api.post("/support/requests", { subject, message, restaurant_id: restaurantId,
                client_request_id: reqId });
            setState("sent");
        } catch (_e) {
            setState("error");    // input kept; same request id → a retry cannot duplicate
        }
    };
    const ready = subject.trim() && message.trim() && state !== "sending";
    return (
        <>
            <button type="button" data-testid={`support-request-open${suffix}`}
                onClick={() => { setOpen(true); onOpen?.(); }}
                className="mx-3 mb-3 flex items-center gap-2 rounded-md px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 transition-colors">
                <LifeBuoy className="h-4 w-4" />
                {t("supportRequest.entry", "Support")}
            </button>
            <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
                <DialogContent className="w-[calc(100vw-2rem)] max-w-lg" data-testid={`support-request-dialog${suffix}`}>
                    <DialogHeader>
                        <DialogTitle>{t("supportRequest.title", "Contact MiseOne support")}</DialogTitle>
                        <DialogDescription>{t("supportRequest.description", "Your message goes to the MiseOne support team together with your restaurant and name.")}</DialogDescription>
                    </DialogHeader>
                    {state === "sent" ? (
                        <p className="text-sm text-emerald-700" role="status" data-testid={`support-request-success${suffix}`}>
                            {t("supportRequest.sent", "Thank you — your request was received. We'll get back to you.")}
                        </p>
                    ) : (
                        <div className="space-y-3">
                            <label className="block text-sm">
                                <span className="font-medium">{t("supportRequest.subject", "Subject")}</span>
                                <Input value={subject} maxLength={200} onChange={(e) => setSubject(e.target.value)}
                                    data-testid={`support-request-subject${suffix}`} className="mt-1" />
                            </label>
                            <label className="block text-sm">
                                <span className="font-medium">{t("supportRequest.message", "Message")}</span>
                                <Textarea value={message} maxLength={5000} rows={6} onChange={(e) => setMessage(e.target.value)}
                                    data-testid={`support-request-message${suffix}`} className="mt-1" />
                            </label>
                            {state === "error" ? (
                                <p className="text-sm text-red-700" role="alert" data-testid={`support-request-error${suffix}`}>
                                    {t("supportRequest.error", "Sending failed. Your message is still here — please try again.")}
                                </p>
                            ) : null}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={close} data-testid={`support-request-close${suffix}`}>
                            {state === "sent" ? t("supportRequest.done", "Close") : t("supportRequest.cancel", "Cancel")}
                        </Button>
                        {state !== "sent" ? (
                            <Button onClick={submit} disabled={!ready} data-testid={`support-request-submit${suffix}`}>
                                {state === "sending" ? t("supportRequest.sending", "Sending…") : t("supportRequest.send", "Send")}
                            </Button>
                        ) : null}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
